import React, { useEffect, useState } from "react";
import { AppDispatch } from "../helpers/appDispatch";
import { useDispatch } from "react-redux";
import { appSelector } from "../helpers/appSelector";
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
} from "antd";
import {
  CreateHallRoomPayload,
  GetHallFloorPayload,
  GetHallRoomPayload,
  HallFloor,
  HallRoom,
} from "../interfaces";
import { clearHallState, getHall, getHalls } from "../store/halls";
import {
  clearRoomState,
  createHallRoom,
  getHallRooms,
  getHallRoomsByHall,
} from "../store/hallRooms";
import { Helmet } from "react-helmet";
import { Content } from "antd/lib/layout/layout";
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  clearFloorState,
  getHallFloor,
  getHallFloors,
} from "../store/hallFloors";
import HallRoomsList from "../components/hallsRooms/HallRoomsList";
import CreateHallRoomForm from "../components/hallsRooms/CreateHallRoomForm";

const HallRooms = () => {
  const dispatch: AppDispatch = useDispatch();

  const { school } = appSelector((state) => state.schoolStore);

  const [form] = Form.useForm();

  const [filteredHallRooms, setFilteredHallRooms] = useState<HallRoom[]>([]);
  const [filteredFloors, setFilteredFloors] = useState<HallFloor[]>([]);
  const [hallOnChangeTriggered, setHallOnChangeTriggered] =
    useState<boolean>(false);
  const [floorOnChangeTriggered, setFloorOnChangeTriggered] =
    useState<boolean>(false);
  const [selectedRoomId, setSelectedRoomId] = useState<string>("");
  const [selectedHallId, setSelectedHallId] = useState<string>("");

  useEffect(() => {
    dispatch(clearHallState());
    dispatch(clearFloorState());
    dispatch(clearRoomState());
  }, []);

  useEffect(() => {
    dispatch(getHalls(school?.id?.toString() || ""));
  }, [school]);

  const {
    hall,
    halls,
    loading: loadingHalls,
  } = appSelector((state) => state.hallsStore);

  const { rooms, loading, isHallRoomSubmitting, success } = appSelector(
    (state) => state.hallRoomStore
  );

  const {
    floors,
    floor,
    loading: loadingHallFloors,
  } = appSelector((state) => state.hallFloorStore);

  useEffect(() => {
    setFilteredHallRooms(rooms);
    setFilteredFloors(floors);
  }, [dispatch, rooms, floors]);

  useEffect(() => {
    form.resetFields();
    success && onCloseModal();
  }, [success]);

  const initialValues: CreateHallRoomPayload = {
    name: "",
    description: "",
    hall_id: hall?.id || "",
    floor_id: floor?.id || "",
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  const onCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOnFinish = (values: CreateHallRoomPayload) => {
    values.hall_id = hall?.id || "";
    values.floor_id = floor?.id || "";
    console.log(values);
    dispatch(createHallRoom(values));
  };

  const onSearch = (value: string) => {
    let filtered = filteredHallRooms.filter(
      (room: HallRoom) =>
        room.name.toLowerCase().includes(value.toLowerCase()) ||
        room.floor?.name
          .toLocaleLowerCase()
          .includes(value.toLocaleLowerCase()) ||
        room.floor?.hall?.name
          .toLocaleLowerCase()
          .includes(value.toLocaleLowerCase())
    );

    setFilteredHallRooms(filtered);
  };

  const handleOnChangeHall = (value: string) => {
    dispatch(clearRoomState());
    dispatch(getHall(value.toString()));
    dispatch(getHallRoomsByHall(value.toString()));
    dispatch(getHallFloors(value.toString()));
    setHallOnChangeTriggered(true);
    setSelectedHallId(value);
  };

  const handleOnChangeFloor = (value: string) => {
    const payload: GetHallRoomPayload = {
      hall_id: selectedHallId,
      floor_id: value,
    };
    dispatch(getHallRooms(payload));

    const getFloorPayload: GetHallFloorPayload = {
      id: value,
      hallId: selectedHallId,
    };

    dispatch(getHallFloor(getFloorPayload));
    setFloorOnChangeTriggered(true);
    setSelectedRoomId(value);
  };

  const onClearHallRooms = () => {
    setFilteredHallRooms([]);
    setFilteredFloors([]);
    setHallOnChangeTriggered(true);
    setFloorOnChangeTriggered(false);
  };

  return (
    <>
      <Helmet>
        <title>Admissions Ghana | Hall Rooms Setup | Blocks</title>
      </Helmet>
      <div className="padding-box">
        <Content className="site-layout-background site-content">
          <div className="margin-top">
            <Row>
              <Col span={24} md={24} xs={24} sm={24}>
                <Space wrap>
                  <Select
                    loading={loadingHalls}
                    placeholder={"Please choose a hall"}
                    style={{ width: 200 }}
                    onChange={(value) => {
                      handleOnChangeHall(value);
                    }}
                  >
                    {halls?.map((hall) => {
                      let roomsCount = hall.rooms?.length || 0;
                      const floorsCount = hall.floors?.length || 0;
                      return (
                        <Select.Option key={hall.id}>
                          {hall.name} ({floorsCount}{" "}
                          {floorsCount === 1 ? "floor" : "floors"}, {roomsCount}{" "}
                          {roomsCount === 1 ? "room" : "rooms"})
                        </Select.Option>
                      );
                    })}
                  </Select>
                  <Select
                    allowClear
                    onClear={() => onClearHallRooms()}
                    loading={loadingHallFloors}
                    placeholder={"Please choose a floor"}
                    style={{ width: 200 }}
                    onChange={(value) => {
                      handleOnChangeFloor(value);
                    }}
                  >
                    {filteredFloors.map((floor) => {
                      const roomsCount = floor.rooms?.length || 0;
                      return (
                        <Select.Option key={floor.id}>
                          {floor.name} ({roomsCount}{" "}
                          {roomsCount === 1 ? "room" : "rooms"})
                        </Select.Option>
                      );
                    })}
                  </Select>
                  <Button
                    disabled={!floorOnChangeTriggered}
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsModalOpen(true)}
                  >
                    Add Room to Selected Floor
                  </Button>
                </Space>
                <Space wrap style={{ float: "right" }}>
                  <Input.Search
                    placeholder="Search by Room / Floor / Hall"
                    onKeyUp={(event) =>
                      onSearch((event.target as HTMLInputElement).value)
                    }
                  />
                </Space>
              </Col>
              <Divider />
            </Row>
          </div>
          {!hallOnChangeTriggered && (
            <Alert
              message="Kindly select a Hall first"
              icon={<InfoCircleOutlined />}
              closable
              type="info"
              showIcon
              style={{ marginBottom: 20 }}
            />
          )}
          <HallRoomsList
            hallRooms={filteredHallRooms}
            loading={loadingHalls || loadingHallFloors || loading}
          />
        </Content>
      </div>

      <CreateHallRoomForm
        open={isModalOpen}
        onCancel={onCloseModal}
        loading={isHallRoomSubmitting}
        form={form}
        initialValues={initialValues}
        onFinish={handleOnFinish}
        hall={hall}
        floor={floor}
      />
    </>
  );
};

export default HallRooms;
